import { Renderer, Program, Mesh, Triangle, Texture, Vec2, Vec3 } from 'https://cdn.jsdelivr.net/npm/ogl@1/+esm';

/*
 * Ambient soft shadows for The Real Sleep.
 *
 * Real depth-based soft shadows (the Daylight / basement.studio technique):
 * an occluder shape is sampled with a golden-angle Vogel disk whose radius and
 * offset grow with the shape's "distance from the wall", so shadows soften and
 * drift with distance the way real light through leaves behaves. The shape is
 * tiled into a gentle scattered canopy and composited with `mix-blend-mode:
 * multiply`, so the shader only outputs "how much shadow" and the page provides
 * color.
 *
 * Tuning tools live in /sandbox (shadow-sandbox.html, shadow-debug.js) and are
 * excluded from theme deploy via .shopifyignore.
 */

const OCCLUDER_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="-280 -95 620 430">
  <path d="M-63.0095 81.4529C-74.6305 45.2359 -124.858 2.60138 -148.52 -14.1888C135.082 56.842 267.599 221.797 298.408 295.396C268.542 262.904 162.876 180.926 110.391 190.39C44.7857 202.221 -48.4832 126.724 -63.0095 81.4529Z" fill="white"/>
  <path d="M-126.156 20.8634C-146.8 -10.8789 -205.987 -37.7671 -233 -47.2435C56.4316 -59.7664 226.425 61.9889 275.242 124.432C238.166 101.529 152.114 61.7762 104.514 85.9898C45.0135 116.257 -100.351 60.5412 -126.156 20.8634Z" fill="white"/>
</svg>`;

const vertex = /* glsl */ `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  varying vec2 vUv;

  uniform sampler2D uOccluder;
  uniform vec2  uResolution;
  uniform float uTime;
  uniform float uScroll;
  uniform vec2  uLight;
  uniform float uDistance;
  uniform float uSoftness;
  uniform float uIntensity;
  uniform float uSampleCount;
  uniform float uTile;
  uniform float uScale;
  uniform float uRotation;
  uniform float uDrift;
  uniform float uParallax;
  uniform float uVignette;
  uniform float uCenterClear;
  uniform vec3  uColor;

  const int MAX_SAMPLES = 64;
  const float GOLDEN = 2.399963229728653; // pi * (3 - sqrt(5))

  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  float aspectRatio() { return uResolution.x / max(uResolution.y, 1.0); }

  // Tile the occluder into a canopy: one shape per cell. Every leaf shares the
  // exact same orientation (uRotation) so they all face one direction — tune
  // uRotation to sit the root on the outside. Only a gentle positional drift
  // varies per cell, for a breeze without breaking the uniform facing.
  float occ(vec2 uv) {
    float ar = aspectRatio();
    vec2 suv = uv;
    suv.x *= ar;
    suv *= uTile;
    vec2 cell = floor(suv);
    vec2 f = fract(suv) - 0.5;

    float rnd = hash21(cell);
    float s = sin(uRotation), c = cos(uRotation);
    vec2 p = mat2(c, -s, s, c) * f;
    p += vec2(sin(uTime * 0.3 + rnd * 10.0), cos(uTime * 0.27 + rnd * 7.0)) * 0.05 * uDrift;
    p /= uScale;
    p += 0.5;
    if (p.x < 0.0 || p.x > 1.0 || p.y < 0.0 || p.y > 1.0) return 0.0;
    return texture2D(uOccluder, vec2(p.x, 1.0 - p.y)).a;
  }

  void main() {
    vec2 uv = vUv;
    vec2 suv = uv;
    suv.y += uScroll * uParallax;

    // Shadow is cast away from the light; distance sets offset, penumbra radius,
    // and how far the canopy lifts off the wall (all scale together).
    vec2 baseOffset = (uv - uLight) * uDistance * 0.6;
    float radius = uDistance * uSoftness * 0.12;
    vec2 canopyLift = (uLight - vec2(0.5)) * uDistance * 0.18;

    float ang = hash21(uv * uResolution) * 6.2831 + uScroll;
    float ca = cos(ang), sa = sin(ang);

    float acc = 0.0;
    float count = 0.0;
    for (int i = 0; i < MAX_SAMPLES; i++) {
      if (float(i) >= uSampleCount) break;
      float fi = float(i);
      float r = radius * sqrt((fi + 0.5) / uSampleCount);
      float theta = fi * GOLDEN;
      vec2 disk = vec2(cos(theta), sin(theta)) * r;
      disk = vec2(ca * disk.x - sa * disk.y, sa * disk.x + ca * disk.y);
      acc += occ(suv + canopyLift - baseOffset + disk);
      count += 1.0;
    }
    float shadow = clamp((acc / max(count, 1.0)) * uIntensity * 1.6, 0.0, 1.0);

    // Bright pool around the light: shadows fade out near the source.
    float distL = length((uv - uLight) * vec2(aspectRatio(), 1.0));
    shadow *= smoothstep(0.0, uVignette, distL);

    // Keep the center of the viewport clear so shadows frame the edges and
    // never sit behind centered content.
    float cDist = length((uv - 0.5) * vec2(aspectRatio(), 1.0));
    shadow *= smoothstep(uCenterClear, uCenterClear + 0.32, cDist);

    float alpha = shadow;
    gl_FragColor = vec4(uColor * alpha, alpha); // premultiplied for multiply blend
  }
`;

function hexToRgb(hex) {
  const clean = (hex || '').replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const int = parseInt(full || '0d332d', 16);
  return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (int & 255) / 255];
}

class AmbientShadows {
  constructor(canvas) {
    this.canvas = canvas;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const d = canvas.dataset;
    this.config = {
      light_x: parseFloat(d.lightX ?? '0.2'),
      light_y: parseFloat(d.lightY ?? '0.12'),
      distance: parseFloat(d.distance ?? '0.45'),
      softness: parseFloat(d.softness ?? '1.2'),
      intensity: parseFloat(d.intensity ?? '0.5'),
      samples: parseFloat(d.samples ?? '32'),
      tile: parseFloat(d.tile ?? '2.2'),
      scale: parseFloat(d.scale ?? '1.1'),
      rotation: parseFloat(d.rotation ?? '0'),
      drift: parseFloat(d.drift ?? '0.6'),
      parallax: parseFloat(d.parallax ?? '0.3'),
      vignette: parseFloat(d.vignette ?? '0.7'),
      center_clear: parseFloat(d.centerClear ?? '0.3'),
      end_x: parseFloat(d.endX ?? '0.05'),
      end_y: parseFloat(d.endY ?? '0.9'),
      end_distance: parseFloat(d.endDistance ?? '0.85'),
      fade_start: parseFloat(d.fadeStart ?? '0.65'),
      color: d.color ? '#' + d.color.replace('#', '') : '#0d332d',
    };

    this.renderer = new Renderer({
      canvas,
      alpha: true,
      premultipliedAlpha: true,
      dpr: Math.min(window.devicePixelRatio || 1, 1.75),
    });
    this.gl = this.renderer.gl;
    this.gl.clearColor(0, 0, 0, 0);

    this.texture = new Texture(this.gl, {
      generateMipmaps: false,
      wrapS: this.gl.CLAMP_TO_EDGE,
      wrapT: this.gl.CLAMP_TO_EDGE,
    });
    this.loadOccluder('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(OCCLUDER_SVG));

    const geometry = new Triangle(this.gl);
    this.program = new Program(this.gl, {
      vertex,
      fragment,
      transparent: true,
      uniforms: {
        uOccluder: { value: this.texture },
        uResolution: { value: new Vec2(1, 1) },
        uTime: { value: 0 },
        uScroll: { value: 0 },
        uLight: { value: new Vec2(this.config.light_x, this.config.light_y) },
        uDistance: { value: this.config.distance },
        uSoftness: { value: this.config.softness },
        uIntensity: { value: this.config.intensity },
        uSampleCount: { value: this.config.samples },
        uTile: { value: this.config.tile },
        uScale: { value: this.config.scale },
        uRotation: { value: this.config.rotation },
        uDrift: { value: this.config.drift },
        uParallax: { value: this.config.parallax },
        uVignette: { value: this.config.vignette },
        uCenterClear: { value: this.config.center_clear },
        uColor: { value: new Vec3(...hexToRgb(this.config.color)) },
      },
    });
    this.program.setBlendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA);
    this.mesh = new Mesh(this.gl, { geometry, program: this.program });

    this.targetScroll = 0;
    this.currentScroll = 0;
    this.pageTarget = 0;   // 0..1 progress through the whole document
    this.pageProgress = 0;
    this.running = false;

    this.resize = this.resize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.loop = this.loop.bind(this);

    window.addEventListener('resize', this.resize, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    });

    this.resize();
    this.onScroll();
    if (this.reducedMotion) {
      this.applyScrollLight();
      this.renderFrame();
    } else {
      this.start();
    }

  }

  loadOccluder(src) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.texture.image = img;
      this.texture.needsUpdate = true;
    };
    img.src = src;
  }

  // Light arc and wall distance share one travel value so they stay in lockstep.
  applyScrollLight() {
    const c = this.config;
    const travel = this.pageProgress;

    const lx = c.light_x + (c.end_x - c.light_x) * travel;
    const ly = c.light_y + (c.end_y - c.light_y) * travel;
    const dist = c.distance + (c.end_distance - c.distance) * travel;

    this.program.uniforms.uLight.value.set(lx, ly);
    this.program.uniforms.uDistance.value = dist;
    this.liveDistance = dist;

    const span = Math.max(0.0001, 1 - c.fade_start);
    const fade = 1 - Math.min(1, Math.max(0, (travel - c.fade_start) / span));
    this.program.uniforms.uIntensity.value = c.intensity * fade;
  }

  resize() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.program.uniforms.uResolution.value.set(this.gl.canvas.width, this.gl.canvas.height);
    if (this.reducedMotion) this.renderFrame();
  }

  onScroll() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    this.targetScroll = max > 0 ? window.scrollY / window.innerHeight : 0;
    this.pageTarget = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
    if (this.reducedMotion) {
      this.pageProgress = this.pageTarget;
      this.applyScrollLight();
      this.renderFrame();
    }
  }

  start() {
    if (this.running || this.reducedMotion) return;
    this.running = true;
    requestAnimationFrame(this.loop);
  }

  stop() { this.running = false; }

  renderFrame() {
    this.program.uniforms.uScroll.value = this.currentScroll;
    this.renderer.render({ scene: this.mesh });
  }

  loop(now) {
    if (!this.running) return;
    this.program.uniforms.uTime.value = now * 0.001;
    this.currentScroll += (this.targetScroll - this.currentScroll) * 0.06;
    this.pageProgress += (this.pageTarget - this.pageProgress) * 0.06;
    this.applyScrollLight();
    this.renderFrame();
    requestAnimationFrame(this.loop);
  }
}

function init() {
  document.querySelectorAll('[data-ambient-shadows]').forEach((canvas) => {
    if (canvas.dataset.initialized) return;
    canvas.dataset.initialized = 'true';
    try {
      new AmbientShadows(canvas);
    } catch (err) {
      console.warn('Ambient shadows failed to initialize:', err);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
